import type { VideoSource } from './source.js'

/**
 * Komunikace s YouTube a Vimeo iframy přes `postMessage`.
 *
 * Původní aplikace na tohle používala `react-player@1.11`, který je od roku 2019
 * neudržovaný. Protokol obou přehrávačů je přitom triviální a stabilní —
 * pár desítek řádků místo 1,7 MB závislosti.
 *
 * Parsování je vytažené do čistých funkcí, aby šlo testovat bez DOM.
 */

export type EmbedEvent =
  | { kind: 'ready' }
  | { kind: 'playing' }
  | { kind: 'paused' }
  | { kind: 'ended' }
  | { kind: 'error'; message: string }
  /**
   * Průběh pro vlastní časovou osu.
   *
   * `duration` je nepovinná: YouTube posílá délku jen v občasných úplných
   * zprávách, kdežto samotný čas tiká zhruba čtyřikrát za vteřinu ve
   * zkrácených zprávách bez ní. Volající si poslední známou délku pamatuje.
   */
  | { kind: 'progress'; currentTime: number; duration?: number }

/* ------------------------------------------------------------------ *
 * YouTube
 * ------------------------------------------------------------------ */

/** Kódy chyb YouTube IFrame API. */
const YT_ERRORS: Record<number, string> = {
  2: 'Neplatné ID videa',
  5: 'Video nelze přehrát v tomto přehrávači',
  100: 'Video bylo odstraněno nebo je soukromé',
  101: 'Vlastník videa zakázal přehrávání mimo YouTube',
  150: 'Vlastník videa zakázal přehrávání mimo YouTube',
}

/**
 * Číselné ID widgetu, které YouTube očekává v handshaku.
 *
 * POZOR: není to ID videa. Poslání ID videa handshake tiše rozbije —
 * přehrávač pak neposílá žádné stavové události a kurz po videu zamrzne.
 */
export const YOUTUBE_WIDGET_ID = 1

/** Stavy YouTube přehrávače: -1 nezahájeno, 0 konec, 1 hraje, 2 pauza, 3 buffer. */
function fromPlayerState(state: unknown): EmbedEvent | null {
  if (state === 0) return { kind: 'ended' }
  if (state === 1) return { kind: 'playing' }
  if (state === 2) return { kind: 'paused' }
  return null
}

/**
 * Rozebere zprávu od YouTube.
 *
 * Vrací POLE, protože jedna zpráva běžně nese dvě informace naráz: změnu
 * stavu i posun v čase. Dřívější „jedna zpráva = jedna událost" znamenala,
 * že u zpráv se stavem posun zapadl — a protože průběžné zprávy zase
 * neobsahují délku a ta byla povinná, časová osa stála celou dobu na nule.
 */
export function parseYouTubeMessage(data: unknown): EmbedEvent[] {
  let payload: unknown = data
  if (typeof data === 'string') {
    try {
      payload = JSON.parse(data)
    } catch {
      return []
    }
  }
  if (!payload || typeof payload !== 'object') return []

  const msg = payload as { event?: string; info?: unknown }

  // `alreadyInitialized` chodí, když handshake proběhl už dřív —
  // pro nás je to stejná informace jako `onReady`.
  if (
    msg.event === 'onReady' ||
    msg.event === 'initialDelivery' ||
    msg.event === 'alreadyInitialized'
  ) {
    return [{ kind: 'ready' }]
  }

  if (msg.event === 'onError') {
    const code = typeof msg.info === 'number' ? msg.info : Number(msg.info)
    return [{ kind: 'error', message: YT_ERRORS[code] ?? `Chyba přehrávače (${code})` }]
  }

  // `infoDelivery` je to, co přehrávač reálně posílá průběžně.
  // `onStateChange` je starší tvar; podporujeme obojí.
  if (msg.event !== 'infoDelivery' && msg.event !== 'onStateChange') return []

  const info = typeof msg.info === 'object' && msg.info !== null ? msg.info : null
  const state = info ? (info as { playerState?: number }).playerState : msg.info

  const events: EmbedEvent[] = []

  const fromState = fromPlayerState(state)
  if (fromState) events.push(fromState)

  if (info) {
    const { currentTime, duration } = info as { currentTime?: number; duration?: number }
    if (typeof currentTime === 'number' && Number.isFinite(currentTime)) {
      events.push(
        typeof duration === 'number' && duration > 0
          ? { kind: 'progress', currentTime, duration }
          : { kind: 'progress', currentTime },
      )
    }
  }

  return events
}

/* ------------------------------------------------------------------ *
 * Vimeo
 * ------------------------------------------------------------------ */

export function parseVimeoMessage(data: unknown): EmbedEvent[] {
  let payload: unknown = data
  if (typeof data === 'string') {
    try {
      payload = JSON.parse(data)
    } catch {
      return []
    }
  }
  if (!payload || typeof payload !== 'object') return []

  const msg = payload as { event?: string; method?: string; data?: { message?: string } }

  if (msg.event === 'ready') return [{ kind: 'ready' }]
  if (msg.event === 'ended' || msg.event === 'finish') return [{ kind: 'ended' }]
  if (msg.event === 'play' || msg.event === 'playing') return [{ kind: 'playing' }]
  if (msg.event === 'pause') return [{ kind: 'paused' }]
  if (msg.event === 'error') {
    return [{ kind: 'error', message: msg.data?.message ?? 'Video se nepodařilo přehrát' }]
  }

  // Vimeo hlásí posun samostatnou událostí a délku posílá pokaždé
  if (msg.event === 'playProgress' || msg.event === 'timeupdate') {
    const d = msg.data as { seconds?: number; duration?: number } | undefined
    if (d && typeof d.seconds === 'number') {
      return [
        typeof d.duration === 'number' && d.duration > 0
          ? { kind: 'progress', currentTime: d.seconds, duration: d.duration }
          : { kind: 'progress', currentTime: d.seconds },
      ]
    }
  }

  return []
}

/* ------------------------------------------------------------------ *
 * Odesílání příkazů
 * ------------------------------------------------------------------ */

export const YOUTUBE_ORIGIN = 'https://www.youtube-nocookie.com'
export const VIMEO_ORIGIN = 'https://player.vimeo.com'

export function originFor(source: VideoSource): string {
  return source.provider === 'vimeo' ? VIMEO_ORIGIN : YOUTUBE_ORIGIN
}

function post(target: Window, origin: string, payload: unknown): void {
  target.postMessage(JSON.stringify(payload), origin)
}

/**
 * Přihlásí se k odběru událostí.
 *
 * YouTube na jediný `listening` neodpoví — vnitřní inicializace iframu
 * s naším `load` závodí. Handshake se proto musí opakovat, dokud přehrávač
 * nenaběhne; volající to řeší přes `subscribeUntilReady`.
 */
export function subscribe(target: Window, source: VideoSource): void {
  if (source.provider === 'youtube') {
    post(target, YOUTUBE_ORIGIN, {
      event: 'listening',
      id: YOUTUBE_WIDGET_ID,
      channel: 'widget',
    })
  } else if (source.provider === 'vimeo') {
    for (const value of ['ready', 'ended', 'play', 'pause', 'error', 'playProgress']) {
      post(target, VIMEO_ORIGIN, { method: 'addEventListener', value })
    }
  }
}

/** Přetočí na danou sekundu. */
export function seekTo(target: Window, source: VideoSource, seconds: number): void {
  if (source.provider === 'youtube') {
    post(target, YOUTUBE_ORIGIN, { event: 'command', func: 'seekTo', args: [seconds, true] })
  } else if (source.provider === 'vimeo') {
    post(target, VIMEO_ORIGIN, { method: 'setCurrentTime', value: seconds })
  }
}

/** Interval mezi pokusy o handshake a jejich maximální počet. */
export const HANDSHAKE_INTERVAL_MS = 300
export const HANDSHAKE_MAX_ATTEMPTS = 40

/**
 * Opakuje handshake, dokud nezavolá `stop()` vrácená funkce
 * nebo dokud nedojdou pokusy. Vrací funkci pro úklid.
 */
export function subscribeUntilReady(
  getTarget: () => Window | null | undefined,
  source: VideoSource,
): () => void {
  let attempts = 0

  const tick = (): void => {
    const target = getTarget()
    if (target) subscribe(target, source)
    if (++attempts >= HANDSHAKE_MAX_ATTEMPTS) window.clearInterval(id)
  }

  tick()
  const id = window.setInterval(tick, HANDSHAKE_INTERVAL_MS)
  return () => window.clearInterval(id)
}

export function play(target: Window, source: VideoSource): void {
  if (source.provider === 'youtube') {
    post(target, YOUTUBE_ORIGIN, { event: 'command', func: 'playVideo', args: [] })
  } else if (source.provider === 'vimeo') {
    post(target, VIMEO_ORIGIN, { method: 'play' })
  }
}

export function pause(target: Window, source: VideoSource): void {
  if (source.provider === 'youtube') {
    post(target, YOUTUBE_ORIGIN, { event: 'command', func: 'pauseVideo', args: [] })
  } else if (source.provider === 'vimeo') {
    post(target, VIMEO_ORIGIN, { method: 'pause' })
  }
}

/** Ztlumí — používá se, když prohlížeč nepustil přehrávání se zvukem. */
export function mute(target: Window, source: VideoSource): void {
  if (source.provider === 'youtube') {
    post(target, YOUTUBE_ORIGIN, { event: 'command', func: 'mute', args: [] })
  } else if (source.provider === 'vimeo') {
    post(target, VIMEO_ORIGIN, { method: 'setMuted', value: true })
  }
}

/** Zapne zvuk poté, co uživatel potvrdil přehrávání gestem. */
export function unmute(target: Window, source: VideoSource): void {
  if (source.provider === 'youtube') {
    post(target, YOUTUBE_ORIGIN, { event: 'command', func: 'unMute', args: [] })
    post(target, YOUTUBE_ORIGIN, { event: 'command', func: 'setVolume', args: [100] })
  } else if (source.provider === 'vimeo') {
    post(target, VIMEO_ORIGIN, { method: 'setMuted', value: false })
    post(target, VIMEO_ORIGIN, { method: 'setVolume', value: 1 })
  }
}

/** Přijatá zpráva patří tomuhle zdroji? Chrání před cizími `postMessage`. */
export function isFromSource(eventOrigin: string, source: VideoSource): boolean {
  if (source.provider === 'youtube') {
    return eventOrigin === YOUTUBE_ORIGIN || eventOrigin === 'https://www.youtube.com'
  }
  if (source.provider === 'vimeo') return eventOrigin === VIMEO_ORIGIN
  return false
}

export function parseMessage(source: VideoSource, data: unknown): EmbedEvent[] {
  if (source.provider === 'youtube') return parseYouTubeMessage(data)
  if (source.provider === 'vimeo') return parseVimeoMessage(data)
  return []
}
